import React, { forwardRef, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useEffectOnce, useViewport } from '@chilecompra/react-kit'
import { Input, Pagination, Table, TableRow, Typography } from '@chilecompra/react-kit/components'
import useDataTable from './DataTable.hook'
import { SORT_DIRECTIONS_DICTIONARY } from './DataTable.utils'
import { GridContainer, GridItem, Results, Root, SearchIcon, SortIcon, TableCell } from './DataTable.styles'

/**
 * The DataTable's component.
 */
const DataTable = forwardRef((props, ref) => {
  const {
    customPagination,
    enableResults,
    footer,
    header,
    headers: initialHeaders,
    headersCellsProps: initialHeadersCellsProps,
    headersDecorators,
    initialPage,
    initialRows,
    loading,
    margin,
    padding,
    records: initialRecords,
    recordsCellsProps,
    recordsDecorators,
    onChangePage,
    onSortColumn
  } = props
  const { size } = useViewport()
  const {
    columnsValue: columns,
    enableFilterValue: enableFilter,
    initialDirectionValue: initialDirection,
    headersValues: headers,
    headersCellsPropsValues: headersCellsProps,
    pageValue: page,
    pagesValue: pages,
    searchValue: search,
    sortColumnValue: sortColumn,
    sortDirectionValue: sortDirection,
    sortHoverValue: sortHover,
    onChangePage: changePage,
    onChangeSearch,
    onClickSort,
    onGetCurrentPage,
    onHoverSort,
    onSetPage
  } = useDataTable({
    initialHeaders,
    initialHeadersCellsProps,
    initialPage,
    initialRows,
    initialRecords: customPagination?.records ? [] : initialRecords
  })

  const [counter, setCounter] = useState(0)
  const handleChangePage = (...args) => {
    changePage(...args)
    onChangePage(...args)

    if (!customPagination?.onChangePage) {
      return
    }

    customPagination?.onChangePage(...args)
  }
  const handleClickSort = (index, value) => event => {
    onClickSort(index, value)(event)
    onSortColumn(event, value)
    setTimeout(() => setCounter(currentValue => currentValue + 1), 0)
  }
  const showPagination = useMemo(() => {
    if (customPagination?.pages) {
      return customPagination?.pages > 1
    }

    return pages > 1
  }, [customPagination, pages])
  const totalRecords = customPagination?.records ? customPagination?.records : initialRecords.length
  const records = customPagination?.records ? initialRecords : onGetCurrentPage()

  useEffectOnce(() => {
    if (!ref) {
      return
    }

    ref.current = { setPage: onSetPage }
  })

  useEffect(() => {
    if (!customPagination?.onSortColumn || !sortColumn || !sortDirection || !counter) {
      return
    }

    customPagination?.onSortColumn(sortColumn, sortDirection)
  }, [counter])

  useEffect(() => {
    if (customPagination?.page === 1) onSetPage(customPagination?.page)
  }, [customPagination?.page])

  return (
    <Root margin={margin} padding={padding}>
      {(enableFilter || !!header) && (
        <GridContainer justifyContent="center">
          <GridItem md={9} xs={12}>
            {header && header}
          </GridItem>
          <GridItem md={3} xs={12}>
            {enableFilter && (
              <GridContainer justifyContent="flex-end">
                <GridItem align="right" xs={12}>
                  <GridContainer alignItems="center" justifyContent="flex-end">
                    <GridItem align="right" padding="0 15px 0 0" md={4} xs={2}>
                      <Typography color="black1" fontSize="16px" fontWeight="bold" lineHeight="21px" variant="body1">
                        Buscar
                      </Typography>
                    </GridItem>
                    <GridItem align="right" md={8} xs={10}>
                      <Input
                        label=" "
                        suffix={<SearchIcon color="50" />}
                        value={search}
                        width="100%"
                        onChange={onChangeSearch}
                      />
                    </GridItem>
                  </GridContainer>
                </GridItem>
              </GridContainer>
            )}
          </GridItem>
        </GridContainer>
      )}
      <GridContainer overflowX="auto">
        <GridItem xs={12}>
          <Table enableStripped loading={loading} totalLoadingBars={10}>
            <TableRow enableHead>
              {headers.map((item, index) => {
                const cellProps = headersCellsProps[index] || { enableSort: false }
                const decorator = headersDecorators[index] || (value => value)

                return (
                  <TableCell
                    {...cellProps}
                    header
                    key={`datatable__header__${index}`}
                    verticalAlign="middle"
                    onClick={handleClickSort(index, item.value)}
                    onMouseLeave={onHoverSort(`${index}__leave`, null)}
                    onMouseOver={onHoverSort(`${index}__over`, item.value)}
                  >
                    {decorator(item.label, index)}
                    {cellProps?.enableSort && sortColumn === item.value && <SortIcon direction={sortDirection} />}
                    {cellProps?.enableSort && sortColumn !== item.value && sortHover === item.value && (
                      <SortIcon direction={initialDirection} opacity="0.5" />
                    )}
                  </TableCell>
                )
              })}
            </TableRow>
            {records.map((item, rowIndex) => (
              <TableRow key={`datatable__records__row__${rowIndex}}`}>
                {Array(columns)
                  .fill(null)
                  .map((_, columnIndex) => {
                    const cellProps = recordsCellsProps[columnIndex] || {}
                    const decorator = recordsDecorators[columnIndex] || (value => value)

                    return (
                      <TableCell key={`datatable__record__row__${rowIndex}__column__${columnIndex}`} {...cellProps}>
                        {decorator(item, rowIndex, columnIndex)}
                      </TableCell>
                    )
                  })}
              </TableRow>
            ))}
          </Table>
        </GridItem>
      </GridContainer>
      <GridContainer justifyContent="center" margin="24px 0 0">
        {enableResults && (
          <GridItem md={8} xs={12}>
            <Results>
              Mostrando {records.length} de {totalRecords} resultados
            </Results>
          </GridItem>
        )}
        {!enableResults && !!footer && (
          <GridItem md={!showPagination ? 12 : 8} xs={12}>
            {footer}
          </GridItem>
        )}
        {showPagination && (
          <GridItem md={enableResults || !!footer ? 4 : 12} xs={12}>
            <Pagination
              alignment={size.isUpToSmall ? 'right' : 'center'}
              count={customPagination?.pages ? customPagination?.pages : pages}
              page={customPagination?.page ? customPagination?.page : page}
              size="small"
              onChange={handleChangePage}
            />
          </GridItem>
        )}
      </GridContainer>
    </Root>
  )
})

DataTable.defaultProps = {
  customPagination: null,
  enableResults: false,
  footer: null,
  header: null,
  headers: [],
  headersCellsProps: [],
  headersDecorators: [],
  initialPage: 1,
  initialRows: 10,
  loading: false,
  margin: 0,
  padding: 0,
  records: [],
  recordsCellsProps: [],
  recordsDecorators: [],
  onChangePage: () => undefined,
  onSortColumn: () => undefined
}

DataTable.propTypes = {
  customPagination: PropTypes.shape({
    page: PropTypes.number,
    pages: PropTypes.number,
    records: PropTypes.arrayOf(PropTypes.any),
    onChangePage: PropTypes.func,
    onSortColumn: PropTypes.func
  }),
  enableResults: PropTypes.bool,
  footer: PropTypes.node,
  header: PropTypes.node,
  headers: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.node,
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    })
  ).isRequired,
  headersCellsProps: PropTypes.arrayOf(
    PropTypes.shape({
      ...TableCell.propTypes,
      enableFilter: PropTypes.bool,
      enableSort: PropTypes.bool,
      initialDirection: PropTypes.oneOf(Object.values(SORT_DIRECTIONS_DICTIONARY)),
      initialSort: PropTypes.bool
    })
  ),
  headersDecorators: PropTypes.arrayOf(PropTypes.func),
  initialPage: PropTypes.number,
  initialRows: PropTypes.number,
  loading: PropTypes.bool,
  margin: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  padding: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  records: PropTypes.arrayOf(PropTypes.any),
  recordsCellsProps: PropTypes.arrayOf(PropTypes.shape(TableCell.propTypes)),
  recordsDecorators: PropTypes.arrayOf(PropTypes.func),
  onChangePage: PropTypes.func,
  onSortColumn: PropTypes.func
}

export default DataTable
